import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';

export const Legal = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  return (
    <>
      <SEOHead
        title={`${isEnglish ? 'Legal Notice' : 'Mentions Légales'} - WiseRank.ai`}
        description={isEnglish 
          ? "Legal information about WiseRank.ai"
          : "Informations légales de WiseRank.ai"}
        path="/legal"
      />

      <article className="min-h-screen pt-24 pb-12">
        <section className="section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent premium-gradient">
              {isEnglish ? "Legal Notice" : "Mentions Légales"}
            </h1>

            <div className="prose dark:prose-invert max-w-none">
              {isEnglish ? (
                // English Version
                <>
                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">1. Legal Information</h2>
                    <p>
                      Company Name: CraftSEO (in process of changing to WiseRank)<br />
                      Legal Status: SAS<br />
                      Share Capital: €10,000<br />
                      Registered Office: 31 rue du passeleu, Montreuil, 93100<br />
                      Phone: 0744958400<br />
                      Email: hello@craftseo.ai<br />
                      SIRET Number: 932902307<br />
                      VAT Number: FR77932902307
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">2. Publication Director</h2>
                    <p>
                      Name: Alexandre Zana<br />
                      Email: hello@craftseo.ai
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">3. Hosting Provider</h2>
                    <p>
                      Name: Hostinger International Ltd.<br />
                      Address: 61 Lordou Vironos Street, 6023 Larnaca, Cyprus<br />
                      Phone: +370 645 03378<br />
                      Website: www.hostinger.com
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">4. Intellectual Property</h2>
                    <p>
                      All elements of the CraftSEO.ai website, including texts, presentations, illustrations, photographs, architecture, and layouts are, except for public documents and additional specifications, the exclusive intellectual property of CraftSEO.ai or its partners. Their representation, reproduction, embedding, distribution, and redistribution, partial or total, are prohibited in accordance with Article L.122-4 of the Intellectual Property Code.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">5. Personal Data</h2>
                    <p>
                      Information collected on the CraftSEO.ai website is processed to improve customer relationship management and provide requested services. The data controller is CraftSEO.ai, represented by the publication director.
                    </p>
                    <p className="mt-4">Collected data includes:</p>
                    <ul className="list-disc pl-6 mt-2">
                      <li>Name and surname</li>
                      <li>Email address</li>
                      <li>Phone number</li>
                      <li>IP address</li>
                      <li>Navigation data (cookies)</li>
                    </ul>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">6. Cookies</h2>
                    <p>
                      The CraftSEO.ai website may request your acceptance of cookies for statistical and display purposes. A cookie is information stored on your hard drive by the server of the site you are visiting.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">7. Hyperlinks</h2>
                    <p>
                      The CraftSEO.ai website may offer links to other websites or resources available on the Internet. CraftSEO.ai has no means to control these sites and external sources.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">8. Disputes</h2>
                    <p>
                      These terms of the CraftSEO.ai website are governed by French law, and any disputes arising from their interpretation or execution will be under the exclusive jurisdiction of the courts where the company's registered office is located.
                    </p>
                  </section>
                </>
              ) : (
                // French Version
                <>
                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">1. Informations Légales</h2>
                    <p>
                      Nom de la société : CraftSEO (en cours de changement de nom pour WiseRank)<br />
                      Statut juridique : SAS<br />
                      Capital social : 10 000€<br />
                      Adresse du siège social : 31 rue du passeleu, Montreuil, 93100<br />
                      Téléphone : 0744958400<br />
                      Email : hello@craftseo.ai<br />
                      Numéro SIRET : 932902307<br />
                      Numéro de TVA intracommunautaire : FR77932902307
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">2. Directeur de la Publication</h2>
                    <p>
                      Nom du directeur de la publication : Alexandre Zana<br />
                      Email du directeur de la publication : hello@craftseo.ai
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">3. Hébergeur</h2>
                    <p>
                      Nom de l'hébergeur : Hostinger International Ltd.<br />
                      Adresse de l'hébergeur : 61 Lordou Vironos Street, 6023 Larnaca, Chypre<br />
                      Téléphone de l'hébergeur : +370 645 03378<br />
                      Site web de l'hébergeur : www.hostinger.com
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">4. Propriété Intellectuelle</h2>
                    <p>
                      L'ensemble des éléments du site CraftSEO.ai, notamment les textes, présentations, illustrations, photographies, arborescences et mises en forme sont, sauf documents publics et précisions complémentaires, la propriété intellectuelle exclusive de CraftSEO.ai ou de ses partenaires. À ce titre, leurs représentations, reproductions, imbrications, diffusions et rediffusions, partielles ou totales, sont interdites conformément aux dispositions de l'article L.122-4 du Code de la propriété intellectuelle.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">5. Données Personnelles</h2>
                    <p>
                      Les informations recueillies sur le site CraftSEO.ai font l'objet d'un traitement destiné à améliorer la gestion de la relation client et à vous fournir les services demandés. Le responsable du traitement des données est CraftSEO.ai, représenté par le directeur de la publication.
                    </p>
                    <p className="mt-4">Les données collectées sont les suivantes :</p>
                    <ul className="list-disc pl-6 mt-2">
                      <li>Nom et prénom</li>
                      <li>Adresse électronique</li>
                      <li>Numéro de téléphone</li>
                      <li>Adresse IP</li>
                      <li>Données de navigation (cookies)</li>
                    </ul>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">6. Cookies</h2>
                    <p>
                      Le site CraftSEO.ai peut être amené à vous demander l'acceptation des cookies pour des besoins de statistiques et d'affichage. Un cookie est une information déposée sur votre disque dur par le serveur du site que vous visitez.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">7. Liens Hypertextes</h2>
                    <p>
                      Le site CraftSEO.ai peut offrir des liens vers d'autres sites internet ou d'autres ressources disponibles sur Internet. CraftSEO.ai ne dispose d'aucun moyen pour contrôler les sites en connexion avec ses sites internet.
                    </p>
                  </section>

                  <section className="mb-12">
                    <h2 className="text-2xl font-bold mb-4">8. Litiges</h2>
                    <p>
                      Les présentes conditions du site CraftSEO.ai sont régies par les lois françaises et toute contestation ou litiges qui pourraient naître de l'interprétation ou de l'exécution de celles-ci seront de la compétence exclusive des tribunaux dont dépend le siège social de la société.
                    </p>
                  </section>
                </>
              )}
            </div>
          </motion.div>
        </section>
      </article>
    </>
  );
};